/// <amd-module name="VSS/Redux/Modules/TTIMonitor/TTIMonitorSaga" />
import { effects, SagaIterator } from "redux-saga";
import { MetaTypes } from "redux/Actions/MetaTypes";
import { Action, ActionsOfType } from "redux/Utils/ActionHelper";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssPerformanceService, TelemetryEventData, Timing } from "VSS/Platform/Performance";
import { isPageInteractive as selector } from "VSS/Redux/Modules/TTIMonitor/TTIMonitorSelector";
import { TTIMonitorActions, TTIMonitorActionTypes } from "./TTIMonitorActions";

/**
 *  Monitors all actions for validation
 */
export function* monitorForActionValidation(serviceInstanceType?: string): SagaIterator {
    yield effects.takeEvery(TTIMonitorActionTypes.AddSplit, addSplitTiming);
    yield effects.takeEvery(TTIMonitorActionTypes.MarkTTI, markTTIScenario, serviceInstanceType);
    yield effects.takeEvery(TTIMonitorActionTypes.StartMeasure, startMeasure);
    yield effects.takeEvery(TTIMonitorActionTypes.EndMeasure, endMeasure);
}

/**
 * Makes sure that only TTI actions are dispatched during TTI
 */
export function* validateTTIActions(action: Action<string>): SagaIterator {
    const isPageInteractive = (yield effects.select(selector)) as any;
    if (action.meta && action.meta[MetaTypes.TTI_ACTION] === MetaTypes.TTI_ACTION && isPageInteractive) {
        throw new Error(`TTI Action is not allowed after TTI - ${action.type}`);
    }

    if ((!action.meta || action.meta[MetaTypes.TTI_ACTION] !== MetaTypes.TTI_ACTION) && !isPageInteractive) {
        throw new Error(`Non-TTI Action is not allowed before TTI - ${action.type}`);
    }
}

export function* addSplitTiming(action: ActionsOfType<TTIMonitorActions, TTIMonitorActionTypes.AddSplit>): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const performanceSerivce: IVssPerformanceService = (yield effects.call([pageContext, pageContext.getService], "IVssPerformanceService")) as any;

    performanceSerivce.addSplitTiming(action.payload.timingName);
}

export function* markTTIScenario(
    serviceInstanceType: string | undefined,
    action: ActionsOfType<TTIMonitorActions, TTIMonitorActionTypes.MarkTTI>
): SagaIterator {
    const pageContext: IVssPageContext = (yield effects.getContext("pageContext")) as any;
    const performanceService: IVssPerformanceService = (yield effects.call([pageContext, pageContext.getService], "IVssPerformanceService")) as any;

    const additionalTelemetry = {
        serviceInstanceType: serviceInstanceType
    } as TelemetryEventData;

    performanceService.markTimeToInteractive(
        action.payload.featureName,
        action.payload.scenarioName,
        /* endTime */ undefined,
        action.payload.additionalProperties,
        /* splitTimings*/ undefined,
        additionalTelemetry
    );

    // effects.delay causes the TTI state to be set on next tick
    // That makes sure that any legacy content that are waiting for TTI
    // Are executed on the next cycle rather than blocking first Render cycle
    yield effects.delay(1);
    yield effects.put(TTIMonitorActions.setTTIState());
}

export function* startMeasure(action: ActionsOfType<TTIMonitorActions, TTIMonitorActionTypes.StartMeasure>): SagaIterator {
    const startLabel = _getStartLabel(action.payload.measureName);
    yield effects.put(TTIMonitorActions.addSplit(startLabel));
    Timing.mark(startLabel);
}

export function* endMeasure(action: ActionsOfType<TTIMonitorActions, TTIMonitorActionTypes.EndMeasure>): SagaIterator {
    const measureName = action.payload.measureName;
    const startLabel = _getStartLabel(measureName);
    const endLabel = _getEndLabel(measureName);
    yield effects.put(TTIMonitorActions.addSplit(endLabel));
    Timing.mark(endLabel);
    Timing.measure(measureName, startLabel, endLabel);
}

function _getStartLabel(name: string): string {
    return `${name}.Start`;
}

function _getEndLabel(name: string): string {
    return `${name}.End`;
}
